import type { InventoryRowEdit } from '..';
import type { PoolManagementAction } from './actions';
import type { PoolManagementState } from './types';
import { initialPoolManagementState } from './types';

function mergeEdit(prev: InventoryRowEdit | undefined, next: InventoryRowEdit): InventoryRowEdit {
    if (!prev) return next;
    return { ...prev, ...next };
}

function editsEqual(a: InventoryRowEdit, b: InventoryRowEdit): boolean {
    return a.rowId === b.rowId && a.currentQuantity === b.currentQuantity && a.currentLabPrice === b.currentLabPrice;
}

export function poolManagementReducer(state: PoolManagementState, action: PoolManagementAction): PoolManagementState {
    switch (action.type) {
        case 'OPEN_POOL_LIST':
            if (state.view === 'list' && state.selectedPoolId === null) return state;
            return {
                ...state,
                view: 'list',
                selectedPoolId: null,
                symbolSearchQuery: '',
                appliedSymbolSearch: '',
                addSymbolDraft: '',
                rowEdits: {},
                pendingRowRemovals: [],
                submitReviewOpen: false,
                isSubmitting: false,
                submitError: null,
            };

        case 'OPEN_POOL_DETAIL':
            if (state.view === 'detail' && state.selectedPoolId === action.poolId) return state;
            return {
                ...state,
                view: 'detail',
                selectedPoolId: action.poolId,
                symbolSearchQuery: '',
                appliedSymbolSearch: '',
                addSymbolDraft: '',
                rowEdits: {},
                pendingRowRemovals: [],
                submitReviewOpen: false,
                isSubmitting: false,
                submitError: null,
                // Always land on the Permissions tab when opening a pool,
                // regardless of which tab was active for the previous one.
                activePoolDetailTab: 'permissions',
                // Reset to the first page so the new pool's table opens
                // at the top. Page-size sticks across pool switches.
                inventoryPage: 1,
            };

        case 'SET_POOL_SEARCH_QUERY':
            if (state.poolSearchQuery === action.query) return state;
            return { ...state, poolSearchQuery: action.query };

        case 'SET_SYMBOL_SEARCH_QUERY':
            if (state.symbolSearchQuery === action.query) return state;
            // Narrowing the filter can leave the current page out of range;
            // snap back to page 1 so the user always sees results.
            return { ...state, symbolSearchQuery: action.query, inventoryPage: 1 };

        case 'SET_APPLIED_SYMBOL_SEARCH':
            if (state.appliedSymbolSearch === action.query) return state;
            // The committed term drives the backend query; reset the page
            // too so the (now different) result set opens at the top.
            return { ...state, appliedSymbolSearch: action.query, inventoryPage: 1 };

        case 'SET_INVENTORY_PAGE':
            if (state.inventoryPage === action.page) return state;
            return { ...state, inventoryPage: action.page };

        case 'SET_INVENTORY_ROWS_PER_PAGE':
            if (state.inventoryRowsPerPage === action.rowsPerPage) return state;
            // Changing rows-per-page invalidates the current page index;
            // snap to 1 so the offset math is well-defined.
            return { ...state, inventoryRowsPerPage: action.rowsPerPage, inventoryPage: 1 };

        case 'SET_CHANGE_LOG_SYMBOL_FILTER':
            if (state.changeLogSymbolFilter === action.query) return state;
            return { ...state, changeLogSymbolFilter: action.query };

        case 'SET_CHANGE_LOG_ACTION_FILTER':
            if (state.changeLogActionFilter === action.filter) return state;
            return { ...state, changeLogActionFilter: action.filter };

        case 'SET_CHANGE_LOG_TIME_RANGE':
            if (state.changeLogTimeRange === action.range) return state;
            return { ...state, changeLogTimeRange: action.range };

        case 'SET_ADD_SYMBOL_DRAFT':
            if (state.addSymbolDraft === action.value) return state;
            return { ...state, addSymbolDraft: action.value };

        case 'STAGE_ROW_EDIT': {
            const next = mergeEdit(state.rowEdits[action.edit.rowId], action.edit);
            const prev = state.rowEdits[action.edit.rowId];
            if (prev && editsEqual(prev, next)) return state;
            return { ...state, rowEdits: { ...state.rowEdits, [action.edit.rowId]: next } };
        }

        case 'CLEAR_ROW_FIELD_EDIT': {
            const existing = state.rowEdits[action.rowId];
            if (!existing || existing[action.field] === undefined) return state;
            const nextEdit: InventoryRowEdit = { rowId: existing.rowId };
            if (action.field !== 'currentQuantity' && existing.currentQuantity !== undefined) {
                nextEdit.currentQuantity = existing.currentQuantity;
            }
            if (action.field !== 'currentLabPrice' && existing.currentLabPrice !== undefined) {
                nextEdit.currentLabPrice = existing.currentLabPrice;
            }
            const isEmpty = nextEdit.currentQuantity === undefined && nextEdit.currentLabPrice === undefined;
            const { [action.rowId]: _removed, ...remainingEdits } = state.rowEdits;
            return {
                ...state,
                rowEdits: isEmpty ? remainingEdits : { ...remainingEdits, [action.rowId]: nextEdit },
            };
        }

        case 'STAGE_ROW_REMOVAL':
            if (state.pendingRowRemovals.includes(action.rowId)) return state;
            return { ...state, pendingRowRemovals: [...state.pendingRowRemovals, action.rowId] };

        case 'UNSTAGE_ROW_REMOVAL':
            if (!state.pendingRowRemovals.includes(action.rowId)) return state;
            return {
                ...state,
                pendingRowRemovals: state.pendingRowRemovals.filter((id) => id !== action.rowId),
            };

        case 'RESET_EDITS':
            if (
                Object.keys(state.rowEdits).length === 0 &&
                state.pendingRowRemovals.length === 0 &&
                state.addSymbolDraft === '' &&
                !state.submitReviewOpen &&
                state.submitError === null &&
                !state.isSubmitting
            ) {
                return state;
            }
            // Close the submit-review dialog and clear any failed-submit
            // error / in-flight submitting state: keeping them around would
            // leave a stale dialog, a dead-end error banner, or a submit bar
            // stuck disabled on `isSubmitting === true` after a user reset.
            return {
                ...state,
                rowEdits: {},
                pendingRowRemovals: [],
                addSymbolDraft: '',
                submitReviewOpen: false,
                submitError: null,
                isSubmitting: false,
            };

        case 'CLEAR_STAGED_CHANGES': {
            const editIds = new Set(action.rowEditIds);
            const removedIds = new Set(action.removedRowIds);
            if (editIds.size === 0 && removedIds.size === 0) return state;

            // Only allocate new collections when something actually matches —
            // a non-empty id list whose entries aren't staged shouldn't break
            // the reducer's reference-identity short-circuit.
            let nextEdits: Record<string, InventoryRowEdit> = state.rowEdits;
            let editsChanged = false;
            if (editIds.size > 0) {
                const rebuilt: Record<string, InventoryRowEdit> = {};
                for (const [rowId, edit] of Object.entries(state.rowEdits)) {
                    if (editIds.has(rowId)) editsChanged = true;
                    else rebuilt[rowId] = edit;
                }
                if (editsChanged) nextEdits = rebuilt;
            }

            let nextRemovals: ReadonlyArray<string> = state.pendingRowRemovals;
            let removalsChanged = false;
            if (removedIds.size > 0) {
                const filtered = state.pendingRowRemovals.filter((id) => !removedIds.has(id));
                if (filtered.length !== state.pendingRowRemovals.length) {
                    nextRemovals = filtered;
                    removalsChanged = true;
                }
            }

            if (!editsChanged && !removalsChanged) return state;
            return { ...state, rowEdits: nextEdits, pendingRowRemovals: nextRemovals };
        }

        case 'OPEN_SUBMIT_REVIEW':
            if (state.submitReviewOpen) return state;
            return { ...state, submitReviewOpen: true };

        case 'CLOSE_SUBMIT_REVIEW':
            if (!state.submitReviewOpen) return state;
            return { ...state, submitReviewOpen: false };

        case 'START_SUBMIT':
            if (state.isSubmitting && state.submitError === null) return state;
            return { ...state, isSubmitting: true, submitError: null };

        case 'SUCCEED_SUBMIT':
            if (!state.isSubmitting && state.submitError === null) return state;
            return { ...state, isSubmitting: false, submitError: null };

        case 'FAIL_SUBMIT':
            if (!state.isSubmitting && state.submitError === action.error) return state;
            return { ...state, isSubmitting: false, submitError: action.error };

        case 'SET_ACTIVE_POOL_DETAIL_TAB':
            if (state.activePoolDetailTab === action.tab) return state;
            return { ...state, activePoolDetailTab: action.tab };

        case 'CLEAR_SUBMIT_ERROR':
            if (state.submitError === null) return state;
            return { ...state, submitError: null };

        case 'OPEN_UPLOAD_WIZARD':
            if (state.uploadWizardOpen) return state;
            return {
                ...state,
                uploadWizardOpen: true,
                uploadStep: 'drop',
                uploadFile: null,
                uploadParseResult: null,
                uploadCategoryFilter: 'added',
                uploadSymbolFilter: '',
                uploadMode: 'merge',
                uploadApplying: false,
                uploadError: null,
                uploadAppliedSummary: null,
            };

        case 'CLOSE_UPLOAD_WIZARD':
            if (!state.uploadWizardOpen && !state.uploadApplying && state.uploadError === null) return state;
            // Close drops the entire upload-side lifecycle in one shot so
            // pool-switch / firm-switch callers don't need to know about
            // its individual signals.
            return {
                ...state,
                uploadWizardOpen: false,
                uploadStep: 'drop',
                uploadFile: null,
                uploadParseResult: null,
                uploadCategoryFilter: 'added',
                uploadSymbolFilter: '',
                uploadMode: 'merge',
                uploadApplying: false,
                uploadError: null,
                uploadAppliedSummary: null,
            };

        case 'SET_UPLOAD_STEP':
            if (state.uploadStep === action.step) return state;
            return { ...state, uploadStep: action.step };

        case 'SET_UPLOAD_FILE':
            if (state.uploadFile === action.file && state.uploadParseResult === action.parseResult) return state;
            return {
                ...state,
                uploadFile: action.file,
                uploadParseResult: action.parseResult,
                uploadSymbolFilter: '',
            };

        case 'SET_UPLOAD_CATEGORY_FILTER':
            if (state.uploadCategoryFilter === action.category) return state;
            return { ...state, uploadCategoryFilter: action.category, uploadSymbolFilter: '' };

        case 'SET_UPLOAD_SYMBOL_FILTER':
            if (state.uploadSymbolFilter === action.query) return state;
            return { ...state, uploadSymbolFilter: action.query };

        case 'SET_UPLOAD_MODE':
            if (state.uploadMode === action.mode) return state;
            return { ...state, uploadMode: action.mode };

        case 'START_UPLOAD_APPLY':
            if (state.uploadApplying && state.uploadError === null) return state;
            return { ...state, uploadApplying: true, uploadError: null };

        case 'SUCCEED_UPLOAD_APPLY':
            return {
                ...state,
                uploadApplying: false,
                uploadError: null,
                uploadAppliedSummary: action.summary,
                uploadStep: 'done',
            };

        case 'FAIL_UPLOAD_APPLY':
            return { ...state, uploadApplying: false, uploadError: action.error };

        case 'SET_UPLOAD_ERROR':
            if (state.uploadError === action.error) return state;
            return { ...state, uploadError: action.error };

        default: {
            const _exhaustiveCheck: never = action;
            return state;
        }
    }
}

export { initialPoolManagementState };
